import { type GraphQLAnswer, graphql } from './client';

export enum ActivationRefusal {
    INVALID_LINK = 'INVALID_ACTIVATION_LINK',
    EXPIRED_LINK = 'EXPIRED_ACTIVATION_LINK',
    ALREADY_ACTIVATED = 'ALREADY_ACTIVATED',
    ACCOUNT_DISABLED = 'ACCOUNT_DISABLED',
    IDENTITY_UNAVAILABLE = 'INSIDE_API_UNAVAILABLE',
    PASSWORD_REJECTED = 'PASSWORD_REJECTED',
    // No password sent, but the person has not set one yet: the screen falls back to the form.
    PASSWORD_REQUIRED = 'PASSWORD_REQUIRED',
}

export interface ActivationResult {
    email: string | null;
    organizationNames: string[];
}

// `code` is null for anything the API did not name (network, 500, pre-resolver validation).
export class ActivationError extends Error {
    constructor(readonly code: ActivationRefusal | null) {
        super(code ?? 'UNKNOWN');
    }
}

const MUTATION = `
    mutation ActivateAccount($input: ActivateAccountInput!) {
        activateAccount(input: $input) {
            email
            organizationNames
        }
    }
`;

const isRefusal = (value: unknown): value is ActivationRefusal =>
    Object.values(ActivationRefusal).includes(value as ActivationRefusal);

const INVITEE_QUERY = `
    query ActivationInvitee($token: String!) {
        activationInvitee(token: $token) {
            hasPassword
        }
    }
`;

// Reads, without spending the token, whether the person already has a password.
// Any failure returns false so the screen falls back to the password form.
export const fetchActivationInvitee = async (token: string): Promise<{ hasPassword: boolean }> => {
    try {
        const payload = await graphql<{ activationInvitee?: { hasPassword?: boolean } | null }>(INVITEE_QUERY, {
            token,
        });
        return { hasPassword: payload.data?.activationInvitee?.hasPassword ?? false };
    } catch {
        return { hasPassword: false };
    }
};

export const activateAccount = async (token: string, password?: string): Promise<ActivationResult> => {
    let payload: GraphQLAnswer<{ activateAccount?: ActivationResult | null }>;
    try {
        // Omit password entirely when absent: an empty string fails the schema length constraint.
        payload = await graphql<{ activateAccount?: ActivationResult | null }>(MUTATION, {
            input: password ? { token, password } : { token },
        });
    } catch {
        throw new ActivationError(null);
    }

    const code = payload.errors?.[0]?.extensions?.code;
    if (code) {
        throw new ActivationError(isRefusal(code) ? code : null);
    }
    if (payload.errors?.length) {
        throw new ActivationError(null);
    }

    const result = payload.data?.activateAccount;
    if (!result) {
        throw new ActivationError(null);
    }
    return { email: result.email ?? null, organizationNames: result.organizationNames ?? [] };
};
